package com.havn.app

import com.havn.app.core.media.HavnFilePicker
import com.havn.app.core.media.HavnPhotoPicker
import com.havn.app.core.media.HavnPickedFile
import com.havn.app.core.media.HavnPickedPhoto
import com.havn.app.core.media.decodeHavnDataUrlBytes
import com.havn.app.core.supabase.HavnSupabaseConfig
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.window.ComposeViewport
import kotlinx.browser.document
import kotlinx.browser.window
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Uint8Array
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.HTMLInputElement
import org.w3c.files.FileReader
import kotlin.math.max
import kotlin.math.roundToInt

@OptIn(ExperimentalComposeUiApi::class)
fun main() {
    if (resolveShortShareLinkIfNeeded()) return
    ComposeViewport(viewportContainerId = "ComposeTarget") {
        HavnApp(photoPicker = webPhotoPicker(), filePicker = webFilePicker())
    }
}

private fun resolveShortShareLinkIfNeeded(): Boolean {
    val token = window.location.pathname
        .removePrefix("/s/")
        .substringBefore("/")
        .takeIf { window.location.pathname.startsWith("/s/") && it.matches(Regex("[A-Za-z0-9]{8,64}")) }
        ?: return false
    document.body?.innerHTML = """
        <div style="min-height:100vh;display:grid;place-items:center;background:#05080D;color:#F7F2E8;font:16px -apple-system,BlinkMacSystemFont,Segoe UI,sans-serif;text-align:center;padding:32px;">
            Opening HAVN file...
        </div>
    """.trimIndent()
    redirectToShortShareLink(token, HavnSupabaseConfig.url, HavnSupabaseConfig.anonKey)
    return true
}

@Suppress("UnsafeCastFromDynamic")
private fun redirectToShortShareLink(token: String, supabaseUrl: String, anonKey: String): Unit = js(
    """
    (() => {
        fetch(supabaseUrl + "/rest/v1/rpc/resolve_attachment_share_link", {
            method: "POST",
            headers: {
                "apikey": anonKey,
                "Authorization": "Bearer " + anonKey,
                "Content-Type": "application/json",
                "Accept": "application/json"
            },
            body: JSON.stringify({ p_token: token })
        })
            .then((response) => response.ok ? response.json() : Promise.reject(new Error("Share link unavailable")))
            .then((rows) => {
                const row = Array.isArray(rows) ? rows[0] : rows;
                const target = row && row.target_url;
                const title = row && row.title || "HAVN file";
                if (!target && window.location.hostname === "sandboxedapp.havnre.app") {
                    window.location.replace("https://app.havnre.app/s/" + encodeURIComponent(token));
                    return;
                }
                if (!target) throw new Error("Share link expired or not found");
                if (isHavnImageTarget(target)) {
                    showHavnImageTarget(title, target);
                    return;
                }
                window.location.replace(target);
            })
            .catch((error) => {
                document.body.innerHTML = '<div style="min-height:100vh;display:grid;place-items:center;background:#05080D;color:#F7F2E8;font:16px -apple-system,BlinkMacSystemFont,Segoe UI,sans-serif;text-align:center;padding:32px;">This HAVN link is expired or unavailable.<br><span style="color:#B6B0A5;font-size:13px;">' + String(error && error.message || error || "") + '</span></div>';
            });

        function isHavnImageTarget(url) {
            const lower = String(url || "").toLowerCase();
            return lower.includes("/storage/v1/object/") && (
                lower.includes("property-photos/") ||
                lower.includes("contact-photos/") ||
                lower.includes("image/") ||
                lower.match(/\.(png|jpe?g|webp|gif|avif)(\?|$)/)
            );
        }

        function showHavnImageTarget(title, target) {
            document.body.innerHTML = "";
            const root = document.createElement("main");
            root.style.cssText = "min-height:100vh;box-sizing:border-box;background:#05080D;color:#F7F2E8;font:16px -apple-system,BlinkMacSystemFont,Segoe UI,sans-serif;padding:24px;display:flex;flex-direction:column;align-items:center;gap:18px;";

            const logo = document.createElement("div");
            logo.textContent = "HAVN";
            logo.style.cssText = "color:#D7B55A;letter-spacing:.18em;font-weight:700;font-size:13px;margin-top:10px;";

            const heading = document.createElement("h1");
            heading.textContent = title || "HAVN file";
            heading.style.cssText = "font-size:20px;line-height:1.25;text-align:center;margin:0;max-width:760px;";

            const frame = document.createElement("div");
            frame.style.cssText = "width:min(100%,920px);min-height:260px;border:1px solid rgba(215,181,90,.26);border-radius:24px;background:#101820;display:grid;place-items:center;overflow:hidden;";

            const img = document.createElement("img");
            img.alt = title || "HAVN file";
            img.src = target;
            img.style.cssText = "display:block;max-width:100%;max-height:78vh;object-fit:contain;";
            img.onerror = () => {
                frame.innerHTML = '<div style="padding:32px;text-align:center;color:#B6B0A5;">Preview is unavailable. Open the file directly below.</div>';
            };
            frame.appendChild(img);

            const actions = document.createElement("div");
            actions.style.cssText = "display:flex;flex-wrap:wrap;gap:12px;justify-content:center;";

            const open = document.createElement("a");
            open.href = target;
            open.textContent = "Open image";
            open.target = "_self";
            open.rel = "noopener";
            open.style.cssText = "min-width:148px;text-align:center;text-decoration:none;color:#05080D;background:#D7B55A;border-radius:999px;padding:13px 18px;font-weight:700;";

            const download = document.createElement("button");
            download.type = "button";
            download.textContent = "Download";
            download.style.cssText = "min-width:148px;text-align:center;color:#F7F2E8;background:transparent;border:1px solid rgba(247,242,232,.34);border-radius:999px;padding:13px 18px;font:700 16px -apple-system,BlinkMacSystemFont,Segoe UI,sans-serif;cursor:pointer;";
            download.onclick = () => downloadHavnImage(target, title || "HAVN file", download);

            actions.appendChild(open);
            actions.appendChild(download);
            root.appendChild(logo);
            root.appendChild(heading);
            root.appendChild(frame);
            root.appendChild(actions);
            document.body.appendChild(root);
        }

        function downloadHavnImage(target, title, button) {
            const originalText = button.textContent;
            button.disabled = true;
            button.textContent = "Downloading...";
            fetch(target)
                .then((response) => {
                    if (!response.ok) throw new Error("Could not download image");
                    return response.blob();
                })
                .then((blob) => {
                    const objectUrl = URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = objectUrl;
                    link.download = havnDownloadFileName(title, blob.type);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
                })
                .catch(() => {
                    window.location.href = target;
                })
                .finally(() => {
                    button.disabled = false;
                    button.textContent = originalText;
                });
        }

        function havnDownloadFileName(title, contentType) {
            const safeTitle = String(title || "havn-file")
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-+|-+$/g, "")
                || "havn-file";
            const type = String(contentType || "").toLowerCase();
            const extension = type.includes("png") ? "png"
                : type.includes("webp") ? "webp"
                : type.includes("avif") ? "avif"
                : "jpg";
            return safeTitle + "." + extension;
        }
    })()
    """
)

private fun webPhotoPicker(): HavnPhotoPicker = HavnPhotoPicker { onResult ->
    val input = document.createElement("input") as HTMLInputElement
    input.type = "file"
    input.accept = "image/*"
    input.style.display = "none"
    input.onchange = {
        val file = input.files?.item(0)
        if (file == null) {
            onResult(null)
            input.remove()
        } else {
            val bytesReader = FileReader()
            bytesReader.onload = {
                val arrayBuffer = bytesReader.result as ArrayBuffer
                val bytes = Uint8Array(arrayBuffer).toByteArray()
                val dataReader = FileReader()
                dataReader.onload = {
                    val dataUrl = dataReader.result as? String
                    createOptimizedPhotoPreview(
                        dataUrl = dataUrl,
                        maxWidth = HavnUploadDisplayMaxWidth,
                        jpegQuality = HavnUploadDisplayJpegQuality
                    ) { display ->
                        createOptimizedPhotoPreview(
                            dataUrl = dataUrl,
                            maxWidth = HavnUploadListThumbnailMaxWidth,
                            jpegQuality = HavnUploadListThumbnailJpegQuality
                        ) { list ->
                            val displayBytes = display?.dataUrl?.decodeHavnDataUrlBytes()
                            onResult(
                                HavnPickedPhoto(
                                    fileName = file.name.ifBlank { "havn-photo.jpg" },
                                    bytes = displayBytes?.takeIf { it.isNotEmpty() } ?: bytes,
                                    contentType = if (displayBytes != null) "image/jpeg" else file.type.ifBlank { "image/jpeg" },
                                    thumbnailDataUrl = display?.dataUrl ?: dataUrl,
                                    thumbnailWidth = display?.width,
                                    thumbnailHeight = display?.height,
                                    listThumbnailDataUrl = list?.dataUrl ?: display?.dataUrl ?: dataUrl,
                                    listThumbnailWidth = list?.width ?: display?.width,
                                    listThumbnailHeight = list?.height ?: display?.height
                                )
                            )
                            input.remove()
                        }
                    }
                }
                dataReader.onerror = {
                    onResult(null)
                    input.remove()
                }
                dataReader.readAsDataURL(file)
            }
            bytesReader.onerror = {
                onResult(null)
                input.remove()
            }
            bytesReader.readAsArrayBuffer(file)
        }
    }
    document.body?.appendChild(input)
    input.click()
}

private fun webFilePicker(): HavnFilePicker = HavnFilePicker { onResult ->
    val input = document.createElement("input") as HTMLInputElement
    input.type = "file"
    input.style.display = "none"
    input.onchange = {
        val file = input.files?.item(0)
        if (file == null) {
            onResult(null)
            input.remove()
        } else {
            val reader = FileReader()
            reader.onload = {
                val arrayBuffer = reader.result as ArrayBuffer
                onResult(
                    HavnPickedFile(
                        fileName = file.name.ifBlank { "attachment" },
                        bytes = Uint8Array(arrayBuffer).toByteArray(),
                        contentType = file.type.ifBlank { "application/octet-stream" }
                    )
                )
                input.remove()
            }
            reader.onerror = {
                onResult(null)
                input.remove()
            }
            reader.readAsArrayBuffer(file)
        }
    }
    document.body?.appendChild(input)
    input.click()
}

private data class OptimizedPhotoPreview(
    val dataUrl: String,
    val width: Int,
    val height: Int
)

private fun createOptimizedPhotoPreview(
    dataUrl: String?,
    maxWidth: Int,
    jpegQuality: Double,
    onResult: (OptimizedPhotoPreview?) -> Unit
) {
    if (dataUrl.isNullOrBlank()) {
        onResult(null)
        return
    }
    val image = document.createElement("img") as HTMLImageElement
    image.onload = {
        val originalWidth = image.naturalWidth.takeIf { it > 0 } ?: image.width
        val originalHeight = image.naturalHeight.takeIf { it > 0 } ?: image.height
        if (originalWidth <= 0 || originalHeight <= 0) {
            onResult(null)
        } else {
            val scale = minOf(1.0, maxWidth / max(originalWidth, originalHeight).toDouble())
            val width = max(1, (originalWidth * scale).roundToInt())
            val height = max(1, (originalHeight * scale).roundToInt())
            val canvas = document.createElement("canvas") as HTMLCanvasElement
            canvas.width = width
            canvas.height = height
            canvas.getContext("2d")
                ?.asDynamic()
                ?.drawImage(image, 0, 0, width, height)
            onResult(
                OptimizedPhotoPreview(
                    dataUrl = canvas.toDataURL("image/jpeg", jpegQuality),
                    width = width,
                    height = height
                )
            )
        }
        Unit
    }
    image.onerror = { _, _, _, _, _ ->
        onResult(null)
        Unit
    }
    image.src = dataUrl
}

private const val HavnUploadDisplayMaxWidth = 1_080
private const val HavnUploadDisplayJpegQuality = 0.82
private const val HavnUploadListThumbnailMaxWidth = 360
private const val HavnUploadListThumbnailJpegQuality = 0.72

private fun Uint8Array.toByteArray(): ByteArray =
    ByteArray(length) { index -> (this.asDynamic()[index] as Int).toByte() }
